import React, { useState, useMemo, useEffect } from "react";

/**
 * Reusable WeekCalendar Component
 * Displays a calendar where only available dates are clickable
 * 
 * @param {Array} weeks - Array of week labels (e.g., [20250901, 20250907, ...])
 * @param {string|number} currentWeek - Currently selected week
 * @param {function} setCurrentWeek - Function to update the selected week
 * @param {string} placeholder - Placeholder text when no week is selected (default: 'Select Date')
 * @param {string} width - CSS width value for the calendar (default: '320px')
 * @param {boolean} disabled - Whether the calendar is disabled (default: false)
 */
const WeekCalendar = ({
    weeks = [],
    currentWeek,
    setCurrentWeek,
    placeholder = 'Select Date',
    width = '320px',
    disabled = false
}) => {
    /**
     * Convert numeric week label to Date object
     * @param {string|number} weekLabel - Week label like 20250901
     * @returns {Date} - Date object
    */
    console.log('WeekCalendar: Rendering with weeks:', weeks, 'currentWeek:', currentWeek);
    const parseWeekLabel = (weekLabel) => {
        if (!weekLabel) return null;
        const str = weekLabel.toString();
        if (str.length === 8) {
            const year = parseInt(str.substring(0, 4));
            const month = parseInt(str.substring(4, 6)) - 1; // JS months are 0-indexed
            const day = parseInt(str.substring(6, 8));
            return new Date(year, month, day);
        }
        return null;
    };

    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(() => {
        // If we have a selected week, show that month
        if (currentWeek) {
            const date = parseWeekLabel(currentWeek);
            if (date) {
                return new Date(date.getFullYear(), date.getMonth(), 1);
            }
        }

        // If no selected week but we have weeks data, show the latest available month
        if (weeks && weeks.length > 0) {
            const latestWeek = weeks[weeks.length - 1];
            const date = parseWeekLabel(latestWeek);
            if (date) {
                return new Date(date.getFullYear(), date.getMonth(), 1);
            }
        }

        // Fallback to current month (should rarely happen)
        return new Date();
    });

    // Update currentMonth when currentWeek or weeks change
    useEffect(() => {
        // If we have a selected week, show that month
        if (currentWeek) {
            const date = parseWeekLabel(currentWeek);
            if (date) {
                const selectedMonth = new Date(date.getFullYear(), date.getMonth(), 1);
                setCurrentMonth(selectedMonth);
                return;
            }
        }

        // If no selected week but we have weeks data, show the latest available month
        if (weeks && weeks.length > 0) {
            const latestWeek = weeks[weeks.length - 1];
            const date = parseWeekLabel(latestWeek);
            if (date) {
                const latestMonth = new Date(date.getFullYear(), date.getMonth(), 1);
                setCurrentMonth(latestMonth);
            }
        }
    }, [currentWeek, weeks]);

    /**
     * Convert Date object to week label format
     * @param {Date} date - Date object
     * @returns {string} - Week label like 20250901
     */
    const formatDateToWeekLabel = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    };

    /**
     * Format week label to readable date format
     * @param {string|number} weekLabel - Week label like 20250901
     * @returns {string} - Formatted date like "Sep 1, 2025"
     */
    const formatWeekLabel = (weekLabel) => {
        const date = parseWeekLabel(weekLabel);
        if (!date) return '';
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Create a Set of available dates for quick lookup
    const availableDates = useMemo(() => {
        return new Set(weeks.map(week => week.toString()));
    }, [weeks]);

    // Get date range for navigation restrictions
    const dateRange = useMemo(() => {
        if (!weeks || weeks.length === 0) return null;

        const dates = weeks.map(week => parseWeekLabel(week)).filter(Boolean);
        if (dates.length === 0) return null;

        dates.sort((a, b) => a.getTime() - b.getTime());
        return {
            earliest: dates[0],
            latest: dates[dates.length - 1]
        };
    }, [weeks]);

    // Generate calendar days for current month
    const calendarDays = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);

        // Start from the beginning of the week (Sunday)
        startDate.setDate(startDate.getDate() - startDate.getDay());

        const days = [];
        const current = new Date(startDate);

        // Generate 6 weeks worth of days (42 days)
        for (let i = 0; i < 42; i++) {
            const dayLabel = formatDateToWeekLabel(current);
            const isCurrentMonth = current.getMonth() === month;
            const isAvailable = availableDates.has(dayLabel);
            const isSelected = currentWeek && dayLabel === currentWeek.toString();

            days.push({
                date: new Date(current),
                label: dayLabel,
                day: current.getDate(),
                isCurrentMonth,
                isAvailable,
                isSelected
            });

            current.setDate(current.getDate() + 1);
        }

        return days;
    }, [currentMonth, availableDates, currentWeek]);

    const handleDateSelect = (dayInfo) => {
        if (dayInfo.isAvailable && !disabled) {
            setCurrentWeek(parseInt(dayInfo.label));
            setIsOpen(false);
        }
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const navigateMonth = (direction) => {
        if (!dateRange) return;

        setCurrentMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + direction);

            // Check if new month is beyond available date range
            const newYear = newDate.getFullYear();
            const newMonth = newDate.getMonth();

            const earliestYear = dateRange.earliest.getFullYear();
            const earliestMonth = dateRange.earliest.getMonth();
            const latestYear = dateRange.latest.getFullYear();
            const latestMonth = dateRange.latest.getMonth();

            // Don't allow navigation before earliest month
            if (newYear < earliestYear || (newYear === earliestYear && newMonth < earliestMonth)) {
                return prev;
            }

            // Don't allow navigation after latest month
            if (newYear > latestYear || (newYear === latestYear && newMonth > latestMonth)) {
                return prev;
            }

            return newDate;
        });
    };

    // Check if navigation should be disabled
    const canNavigatePrev = useMemo(() => {
        if (!dateRange) return false;
        const prevMonth = new Date(currentMonth);
        prevMonth.setMonth(currentMonth.getMonth() - 1);
        const prevYear = prevMonth.getFullYear();
        const prevMonthNum = prevMonth.getMonth();
        const earliestYear = dateRange.earliest.getFullYear();
        const earliestMonth = dateRange.earliest.getMonth();
        return !(prevYear < earliestYear || (prevYear === earliestYear && prevMonthNum < earliestMonth));
    }, [currentMonth, dateRange]);

    const canNavigateNext = useMemo(() => {
        if (!dateRange) return false;
        const nextMonth = new Date(currentMonth);
        nextMonth.setMonth(currentMonth.getMonth() + 1);
        const nextYear = nextMonth.getFullYear();
        const nextMonthNum = nextMonth.getMonth();
        const latestYear = dateRange.latest.getFullYear();
        const latestMonth = dateRange.latest.getMonth();
        return !(nextYear > latestYear || (nextYear === latestYear && nextMonthNum > latestMonth));
    }, [currentMonth, dateRange]);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Don't render if no weeks provided
    if (!weeks || weeks.length === 0) {
        console.log('WeekCalendar: No weeks provided, rendering placeholder');
        return (
            <div className="relative inline-block text-left" style={{ width }}>
                <div className="inline-flex w-full justify-between items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-400 border border-gray-200 cursor-not-allowed">
                    No dates available
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                    </svg>
                </div>
            </div>
        );
    }

    return (
        <div className="relative inline-block text-left" style={{ width }}>
            <button
                type="button"
                className={`inline-flex w-full justify-between items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm border border-gray-300 transition-all duration-200 ${
                    disabled 
                        ? 'opacity-50 cursor-not-allowed bg-gray-50' 
                        : 'hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer'
                }`}
                onClick={handleToggle}
                disabled={disabled}
            >
                {formatWeekLabel(currentWeek) || placeholder}
                <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z"
                    />
                </svg>
            </button>

            {isOpen && !disabled && (
                <>
                    {/* Backdrop to close calendar */}
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsOpen(false)}
                    />
                    {/* Calendar popup */}
                    <div 
                        className="absolute right-0 z-20 mt-1 rounded-lg bg-white shadow-lg border border-gray-200 p-4 min-w-[280px] md:min-w-[320px]"
                        style={{ width }}
                    >
                        {/* Calendar Header */}
                        <div className="flex justify-between items-center mb-4">
                            <button
                                className={`p-1.5 rounded hover:bg-gray-100 text-gray-600 transition-all flex items-center justify-center ${
                                    !canNavigatePrev ? 'opacity-40 cursor-not-allowed hover:bg-transparent hover:text-gray-600' : ''
                                }`}
                                onClick={() => navigateMonth(-1)}
                                disabled={!canNavigatePrev}
                            >
                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                    <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
                                </svg>
                            </button>

                            <h3 className="text-base font-semibold text-gray-900 m-0">
                                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                            </h3>

                            <button
                                className={`p-1.5 rounded hover:bg-gray-100 text-gray-600 transition-all flex items-center justify-center ${
                                    !canNavigateNext ? 'opacity-40 cursor-not-allowed hover:bg-transparent hover:text-gray-600' : ''
                                }`}
                                onClick={() => navigateMonth(1)}
                                disabled={!canNavigateNext}
                            >
                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                </svg>
                            </button>
                        </div>

                        {/* Day Names */}
                        <div className="grid grid-cols-7 gap-0.5 mb-2">
                            {dayNames.map(day => (
                                <div key={day} className="p-2 text-center text-xs font-semibold text-gray-500 uppercase">
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7 gap-0.5">
                            {calendarDays.map((dayInfo, index) => {
                                let classes = "w-8 h-8 border-none bg-transparent rounded-md text-sm font-medium transition-all duration-200 cursor-pointer flex items-center justify-center ";
                                
                                if (!dayInfo.isCurrentMonth) {
                                    if (dayInfo.isAvailable) {
                                        classes += "text-gray-400 bg-white hover:bg-gray-100 hover:text-gray-600";
                                    } else {
                                        classes += "text-gray-300 cursor-not-allowed bg-transparent";
                                    }
                                } else {
                                    if (dayInfo.isAvailable) {
                                        classes += "text-gray-700 bg-white font-medium hover:bg-blue-50 hover:text-blue-700";
                                    } else {
                                        classes += "text-gray-500 cursor-not-allowed bg-gray-50 opacity-40";
                                    }
                                }

                                if (dayInfo.isSelected) {
                                    classes += " !bg-blue-500 !text-white !font-semibold hover:!bg-blue-600";
                                }

                                return (
                                    <button
                                        key={index}
                                        className={classes}
                                        onClick={() => handleDateSelect(dayInfo)}
                                        disabled={!dayInfo.isAvailable}
                                    >
                                        {dayInfo.day}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default WeekCalendar;